import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeletons } from "@/components/common/EmptyState";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { CreateDrawer } from "@/components/common/CreateDrawer";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl, uploadFile } from "@/lib/api";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Images, Heart, DownloadSimple, ShareNetwork, UploadSimple, Trash, PencilSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const FILTERS = ["All", "Image", "Video", "PDF", "File"];
const META_FIELDS = [
  { name: "name", label: "Name", required: true },
  { name: "brand", label: "Brand", type: "select" },
  { name: "tagsStr", label: "Tags (comma separated)" },
  { name: "usage_rights", label: "Usage Rights", type: "select", options: ["Cleared", "Expiring soon", "Pending", "Restricted"] },
];

export default function Assets() {
  const { brand } = useApp();
  const { isApprover } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef();
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [editItem, setEditItem] = useState(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["assets-all", brand],
    queryFn: async () => (await api.get("/assets-all", { params: brand !== "All brands" ? { brand } : {} })).data,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["assets-all"] });

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) await uploadFile(f, { brand: brand !== "All brands" ? brand : undefined, tags: "upload" });
      toast.success(`${files.length} file(s) uploaded`); invalidate();
    } catch (err) { toast.error("Upload failed"); }
    setUploading(false); e.target.value = "";
  };

  const download = async (a) => {
    try {
      const url = fileUrl(a.url || a.storage_path ? (a.url || `/api/files/${a.storage_path}`) : a.url);
      const res = await fetch(a.url && a.url.startsWith("http") ? a.url : fileUrl(a.url), { });
      const blob = await res.blob();
      const burl = URL.createObjectURL(blob);
      const link = document.createElement("a"); link.href = burl; link.download = a.name || "asset"; link.click();
      URL.revokeObjectURL(burl); toast.success("Downloading…");
    } catch { toast.error("Download failed"); }
  };
  const share = async (a) => {
    const url = a.url?.startsWith("http") ? a.url : fileUrl(a.url);
    await navigator.clipboard.writeText(url); toast.success("Shareable link copied");
  };
  const toggleFav = async (a) => {
    if (a.storage_path) await api.patch(`/resource/uploaded_files/${a.id}`, { favorite: !a.favorite });
    else await api.patch(`/resource/assets/${a.id}`, { favorite: !a.favorite });
    invalidate();
  };
  const del = async (a) => {
    try {
      if (a.storage_path) await api.patch(`/resource/uploaded_files/${a.id}`, { is_deleted: true });
      else await api.delete(`/resource/assets/${a.id}`);
      invalidate(); toast.success("Deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    setConfirm(null);
  };
  const saveMeta = async (body) => {
    const tags = (body.tagsStr || "").split(",").map((t) => t.trim()).filter(Boolean);
    const coll = editItem.storage_path ? "uploaded_files" : "assets";
    await api.patch(`/resource/${coll}/${editItem.id}`, { name: body.name, brand: body.brand, tags, usage_rights: body.usage_rights });
    invalidate(); toast.success("Updated");
  };

  const shown = items.filter((a) => (filter === "All" || a.type === filter) && JSON.stringify(a).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <input ref={fileRef} type="file" multiple hidden onChange={onUpload} accept="image/*,video/*,.pdf,.ai,.psd,.zip" data-testid="asset-file-input" />
      <PageHeader title="Digital Assets" subtitle="Upload, tag & manage every creative" icon={Images}
        actions={<Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn" className="rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white"><UploadSimple size={16} weight="bold" className="mr-1" /> {uploading ? "Uploading…" : "Upload"}</Button>} />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => <button key={f} onClick={() => setFilter(f)} data-testid={`asset-filter-${f.toLowerCase()}`} className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${filter === f ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}>{f}</button>)}
        <div className="relative ml-auto max-w-xs flex-1"><MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by tag, brand…" className="rounded-full pl-9" data-testid="asset-search" /></div>
      </div>

      {isLoading ? <Skeletons /> : (
        <div className="columns-1 gap-4 sm:columns-2 lg:columns-3 xl:columns-4">
          {shown.map((a, i) => {
            const isImg = a.type === "Image" || (!a.type && true);
            const src = a.url?.startsWith("http") ? a.url : fileUrl(a.url);
            return (
              <div key={a.id} data-testid="asset-card" className="group relative mb-4 break-inside-avoid overflow-hidden rounded-2xl border border-border" style={{ animation: `fade-up 0.4s ease-out ${i * 0.02}s both` }}>
                {isImg ? <img src={src} alt={a.name} loading="lazy" className="w-full object-cover" style={{ height: `${180 + (i % 3) * 60}px` }} />
                  : <div className="flex h-40 items-center justify-center bg-secondary text-muted-foreground"><Images size={40} weight="duotone" /><span className="ml-2 text-sm">{a.type}</span></div>}
                <div className="absolute inset-0 flex flex-col justify-between bg-gradient-to-t from-black/70 via-transparent to-black/20 p-3 opacity-0 transition-opacity group-hover:opacity-100">
                  <div className="flex justify-end gap-1.5">
                    <button onClick={() => toggleFav(a)} data-testid="asset-fav" className="rounded-full bg-white/20 p-2 backdrop-blur hover:bg-white/30"><Heart size={15} weight={a.favorite ? "fill" : "regular"} className="text-white" /></button>
                    <button onClick={() => { setEditItem({ ...a, tagsStr: (a.tags || []).join(", ") }); }} data-testid="asset-edit" className="rounded-full bg-white/20 p-2 backdrop-blur hover:bg-white/30"><PencilSimple size={15} className="text-white" /></button>
                    <button onClick={() => share(a)} data-testid="asset-share" className="rounded-full bg-white/20 p-2 backdrop-blur hover:bg-white/30"><ShareNetwork size={15} className="text-white" /></button>
                    <button onClick={() => download(a)} data-testid="asset-download" className="rounded-full bg-white/20 p-2 backdrop-blur hover:bg-white/30"><DownloadSimple size={15} className="text-white" /></button>
                    {isApprover && <button onClick={() => setConfirm(a)} data-testid="asset-delete" className="rounded-full bg-white/20 p-2 backdrop-blur hover:bg-brand-pink/60"><Trash size={15} className="text-white" /></button>}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{a.name}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-1"><span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] text-white">{a.brand}</span><StatusBadge status={a.usage_rights} />{(a.tags || []).slice(0, 2).map((t) => <span key={t} className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-white/80">#{t}</span>)}</div>
                  </div>
                </div>
                {a.favorite && <Heart size={16} weight="fill" className="absolute right-3 top-3 text-brand-pink group-hover:opacity-0" />}
              </div>
            );
          })}
        </div>
      )}

      <CreateDrawer open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)} title="Asset" fields={META_FIELDS} mode="edit" initial={editItem} onSubmit={saveMeta} />
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} title="Delete this asset?" onConfirm={() => del(confirm)} />
    </div>
  );
}
